import { createApi } from "@reduxjs/toolkit/query/react";
import { createBaseQueryWithLogoutGuard } from "../rtk-query/common/createBaseQueryWithLogoutGuard";
import { store } from "../app/store";
import { fetchCurrentCachedState } from "../utils/Utils";
import { CLUSTER_OBJECTS, transformFilterData } from "./newClusterSlice";
import { DASHBOARD_CARDS } from "../constants/dashboardCards";
import {
  createInvalidRequestUrlError,
  isValidUrlParameter,
} from "../api/requestUrl";

const getState = () => store.getState();
const selectFromState = (selector) => selector(getState());

export const _authToken = () => {
  const state = getState();
  return `${state?.authReducer?.userData?.tokenType?.toLowerCase()} ${
    state?.authReducer?.userData?.accessToken
  }`;
};

export const _orgClientId = () =>
  selectFromState((state) => state?.orgReducer?.selectedFI?.orgClientId);

export const _hid = () =>
  selectFromState((state) => state?.orgReducer?.healthAnalysisID?.uid);

export const _contextualFilterObj = () =>
  selectFromState((state) => state?.newClusterReducer?.contextualFilterObj);

export const _currentDashboard = () =>
  selectFromState((state) => state?.dashboardReducer?.currentDashboard);

export const _clusterApiKey = () =>
  selectFromState(
    (state) =>
      state?.dashboardReducer?.selectedCard?.clusterObject?.clusterApiKey ??
      state?.dashboardReducer?.selectedCard?.clusterObject?.key,
  );

const getRequiredFilterQueryParams = () => ({
  orgClientId: _orgClientId(),
  hid: _hid(),
  type: _clusterApiKey(),
});

const hasRequiredFilterQueryParams = (queryParams) =>
  ["orgClientId", "hid", "type"].every((key) =>
    isValidUrlParameter(queryParams?.[key]),
  );

const getAuthHeaders = () => ({
  Authorization: _authToken(),
  "Content-Type": "application/json",
});

const hasInitialFilterCache = (cachedInitialFilterData) =>
  cachedInitialFilterData != null &&
  Object.keys(cachedInitialFilterData).length > 0;

const fetchInitialFilterDataFromApi = async () => {
  const queryParams = getRequiredFilterQueryParams();
  if (!hasRequiredFilterQueryParams(queryParams)) return null;

  return store
    .dispatch(
      newInitialFilterDataSlice.endpoints.getInitialFilterData.initiate({
        headers: getAuthHeaders(),
        queryParams,
        payload: createFilterRequestBody(),
      }),
    )
    .unwrap();
};

const getInitialFilterDataSource = async () => {
  const cachedInitialFilterData = fetchCurrentCachedState(
    getNewInitialFilterQueriesCache(),
  )?.getInitialFilterData;

  if (hasInitialFilterCache(cachedInitialFilterData)) {
    return {
      sourceData: cachedInitialFilterData,
      fromApi: false,
    };
  }

  const sourceData = await fetchInitialFilterDataFromApi();
  return {
    sourceData,
    fromApi: true,
  };
};

const resolveFilterDataSource = ({ sourceData, fromApi }) =>
  fromApi ? sourceData?.data : sourceData;

const getCurrentClusterObject = () => CLUSTER_OBJECTS?.[_clusterApiKey()] || {};

const createFilterRequestBody = (payload) => {
  if (payload && typeof payload === "object") {
    return payload;
  }

  return {
    filter: {},
    requid: _hid() === "no-analysis-id" ? "" : _hid(),
    producttype: _currentDashboard()?.KEY,
    gte: "",
    lte: "",
    duration: "",
  };
};

const getNewInitialFilterQueriesCache = () => {
  const state = getState();

  // Prefer the actual reducerPath and keep legacy fallback for safety.
  return (
    state?.[newInitialFilterDataSlice.reducerPath]?.queries ||
    state?.filter_data?.queries ||
    {}
  );
};

export const getGraphObjectsFromInitialFilter = async () => {
  const { legendsKey, zonesKey } = getCurrentClusterObject();

  const { sourceData, fromApi } = await getInitialFilterDataSource();
  const filterData = resolveFilterDataSource({ sourceData, fromApi });
  const graphItems = filterData?.[legendsKey] ?? null;
  const zoneItems = filterData?.[zonesKey] ?? null;

  return { graphItems, zoneItems };
};

export const getContextualObjectsFromInitialFilter = async () => {
  const contextualFilterObj = _contextualFilterObj();
  const { sourceData, fromApi } = await getInitialFilterDataSource();
  const filterData = resolveFilterDataSource({ sourceData, fromApi });
  const filterItems = filterData?.[contextualFilterObj?.filterkey];

  const filterMap = filterItems?.data
    ? new Map(filterItems.data.map((item) => [item.id, item]))
    : null;

  return { filterMap };
};

export const newInitialFilterDataSlice = createApi({
  reducerPath: "new_initial_filter_data_slice",
  baseQuery: createBaseQueryWithLogoutGuard({
    baseUrl: process.env.REACT_APP_API_URL,
  }),
  tagTypes: ["filter_data"],
  endpoints: (build) => ({
    getInitialFilterData: build.query({
      queryFn: async (
        { headers, queryParams, payload } = {},
        api,
        extraOptions,
        baseQuery,
      ) => {
        const missing = ["orgClientId", "hid", "type"].filter(
          (key) => !isValidUrlParameter(queryParams?.[key]),
        );
        if (missing.length) {
          const message = `Cannot load drilldown filters: missing or invalid ${missing.join(", ")}.`;
          console.error(message);
          return {
            error: createInvalidRequestUrlError(message),
          };
        }
        const { orgClientId, hid } = queryParams;
        const type =
          queryParams.type === DASHBOARD_CARDS.TOP_VENDORS
            ? DASHBOARD_CARDS.TOP_MERCHANTS
            : queryParams.type;
        const path = [orgClientId, "drilldown", hid, type, "filter"]
          .map(encodeURIComponent)
          .join("/");

        const result = await baseQuery({
          url: `bank/${path}`,
          method: "POST",
          headers,
          body: createFilterRequestBody(payload),
        });
        if (result.error) return result;
        return {
          ...result,
          data: {
            ...result.data,
            data: transformFilterData(result.data?.data),
          },
        };
      },
    }),
  }),
});

export const { useGetInitialFilterDataQuery } = newInitialFilterDataSlice;
